import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import CloseIcon from '@mui/icons-material/Close'
import { getEnv } from '@/utils/helpers'

const ordoclicIntegrationIframeId = 'ordoclic'

export type GeneratePrescriptionDialogProps = {
  isLoading?: boolean
  onPrescriptionCreated?: (prescriptionId: string) => void
  onClose?: () => void
  open: boolean
  ordoclicPatientId?: string
  entityId?: string
}

type OrdoclicIntegrationProps = {
  onPrescriptionCreated?: (prescriptionId: string) => void
  ordoclicPatientId: string
  entityId?: string

}

function OrdoclicIntegration({ onPrescriptionCreated, ordoclicPatientId, entityId }: OrdoclicIntegrationProps) {
  useEffect(() => {
    function handleOrdoclicMessage(event: MessageEvent) {
      // Check if the origin is from the expected Ordoclic domain
      if (!event.origin.includes('ordoclic.fr')) {
        return;
      }

      const { message, type } = event.data;

      switch (type) {
        case 'prescription-created':
          if (message.batch) {
            message.batch.forEach((obj: any) => {
              if (obj.id) {
                onPrescriptionCreated?.(obj.id);
              }
            });
          } else if (message.prescriptionId) {
            onPrescriptionCreated?.(message.prescriptionId);
          }
          break;
        case 'prescription-deleted':
          console.log('deleted', message);
          break;
        default:
          break;
      }
    }

    window.addEventListener('message', handleOrdoclicMessage);
    return () => {
      window.removeEventListener('message', handleOrdoclicMessage);
    };
  }, [onPrescriptionCreated]);

  return (
    <iframe
      id={ordoclicIntegrationIframeId}
      title="ordoclic-integration"
      src={`${getEnv('VITE_ORDOCLIC_URL')}/v1/lapbatch/launch?integrated=true&auth_type=${getEnv('VITE_ORDOCLIC_SIGNIN') || 'promotal'}&patient=${ordoclicPatientId}&tenant=${getEnv('VITE_ORDOCLIC_TENANT')}${entityId ? `&entityId=${entityId}&billingType=FINESS` : ''}`}
      width="100%"
      height="100%"
      style={{ border: 'none' }}
    />
  )
}

export function GeneratePrescriptionDialog(props: GeneratePrescriptionDialogProps) {
  const { isLoading = false, onClose, onPrescriptionCreated, open, ordoclicPatientId, entityId } = props
  const { t } = useTranslation()

  return (
    <Dialog fullScreen onClose={onClose} open={open}>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography>{t('page.liveConsultation.generateFSPDialog.title')}</Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
              transform: 'translateX(25%)',
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
        {isLoading ? (
          <CircularProgress />
        ) : ordoclicPatientId ? (
          <OrdoclicIntegration onPrescriptionCreated={onPrescriptionCreated} ordoclicPatientId={ordoclicPatientId} entityId={entityId} />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

export default GeneratePrescriptionDialog
